import { MutableRefObject, useEffect } from "react";
import { useGenerativeCatalog } from "./shared/useGenerativeCatalog";

export type ImageGeneratorPendingSession = {
  seasonId?: string;
  seasonLabel?: string;
  macroThemeId?: string;
  colourStoryId?: string;
  patternTrendId?: string;
};

type UseImageGeneratorCatalogArgs = {
  market: string;
  category: string;
  seasonId: string;
  setSeasonId: (value: string) => void;
  genre: string;
  sessionLocked: boolean;
  pendingSessionRef: MutableRefObject<ImageGeneratorPendingSession | null>;
  setSelectedMacroThemeId: (value: string) => void;
  setSelectedColourStoryId: (value: string) => void;
  setSelectedPatternTrendId: (value: string) => void;
  setSelectedPaletteIndex: (value: number) => void;
  setSelectedPreferences: (value: string[]) => void;
};

export const useImageGeneratorCatalog = ({
  market,
  category,
  seasonId,
  setSeasonId,
  genre,
  sessionLocked,
  pendingSessionRef,
  setSelectedMacroThemeId,
  setSelectedColourStoryId,
  setSelectedPatternTrendId,
  setSelectedPaletteIndex,
  setSelectedPreferences,
}: UseImageGeneratorCatalogArgs) => {
  const query = useGenerativeCatalog({
    market,
    category,
    seasonId,
    genre,
    seasonLabelHint: pendingSessionRef.current?.seasonLabel || "",
  });
  const { seasons, catalog, seasonLabel } = query;

  useEffect(() => {
    if (!market || !category) {
      if (!sessionLocked) {
        setSeasonId("");
      }
      return;
    }
    if (query.seasonsLoading) {
      return;
    }
    const pending = pendingSessionRef.current;
    const matched =
      seasons.find(item => item.id === (pending?.seasonId || seasonId)) ||
      seasons.find(
        item =>
          item.label === pending?.seasonLabel ||
          item.id === pending?.seasonLabel
      );
    if (matched) {
      if (matched.id !== seasonId) {
        setSeasonId(matched.id);
      }
      return;
    }
    if (!sessionLocked) {
      setSeasonId(seasons.some(item => item.id === seasonId) ? seasonId : "");
    }
  }, [
    market,
    category,
    query.seasonsLoading,
    seasons,
    seasonId,
    sessionLocked,
    pendingSessionRef,
    setSeasonId,
  ]);

  useEffect(() => {
    if (!catalog || query.catalogLoading) {
      return;
    }
    const pending = pendingSessionRef.current;
    const macros = catalog.macros;
    const pendingMacro =
      macros.find(item => item.id === pending?.macroThemeId) || macros[0];
    if (pending) {
      setSelectedMacroThemeId(pending.macroThemeId || pendingMacro?.id || "");
      if (pending.colourStoryId) {
        const storyIndex = (pendingMacro?.colourStories || []).findIndex(
          story => story.id === pending.colourStoryId
        );
        setSelectedColourStoryId(pending.colourStoryId);
        if (storyIndex >= 0) {
          setSelectedPaletteIndex(storyIndex);
        }
      }
      setSelectedPatternTrendId(
        pending.patternTrendId || pendingMacro?.patternTrends[0]?.id || ""
      );
      pendingSessionRef.current = null;
      return;
    }
    if (!sessionLocked) {
      setSelectedMacroThemeId("");
      setSelectedColourStoryId("");
      setSelectedPatternTrendId("");
      setSelectedPaletteIndex(-1);
      setSelectedPreferences([]);
    }
  }, [
    catalog,
    query.catalogLoading,
    sessionLocked,
    pendingSessionRef,
    setSelectedColourStoryId,
    setSelectedMacroThemeId,
    setSelectedPaletteIndex,
    setSelectedPatternTrendId,
    setSelectedPreferences,
  ]);

  return {
    ...query,
    seasonLabel,
  };
};
